<template>
  <div class="tw:mt-6 tw:mx-4 tw:mb-[60px]">
    <div v-if="isLoading || isExistingPaymentMethodsLoading || isAvailablePaymentMethodsLoading">
      <div class="tw:animate-pulse tw:bg-gray-300 tw:rounded tw:w-full tw:h-80" aria-hidden="true"></div>
    </div>
    <div v-else-if="isError" class="tw:text-red-700" v-html="$t('errors.wrong_data_from_app')" />
    <div v-else class="tw:flex tw:flex-col" :key="selectedAccommodationId">
      <h1 class="tw:text-[28px] tw:font-bold tw:mb-6 tw:mx-auto">{{ $t('check_out_page_billing_info') }}</h1>
      <div class="rc-payment-grid">
        <div class="rc-payment-currency">
          <PaymentCurrencySelector v-if="!zuoraAccountData?.billingAndPayment?.currency" />
        </div>
        <div class="rc-payment-form">
          <h3 class="tw:font-bold tw:mb-1">{{ $t('business_studio_plus_checkout_payment_details_header') }}</h3>
          <div class="tw:px-4 tw:py-5 tw:pb-4 tw:px-4 tw:bg-red-200 tw:mb-4 tw:rounded-lg" v-if="errorInfo">
            <p class="tw:text-sm">{{ $t(errorInfo) }}</p>
          </div>
          <BaseCard noPadding>
            <PaymentMethodForm
              source="rate-connect-form"
              :currency="currencyCode"
              :accommodationId="selectedAccommodationId"
              :productName="zuoraMetricConstants.productName.rateConnect"
              :fetchingPaymentMethods="isFetching || isExistingPaymentMethodsLoading"
              :existingPaymentMethods="existingPaymentMethods"
              :availablePaymentMethods="availablePaymentMethodsData"
              :paypalConfig="paypalConfig"
              :selectedPaymentMethod="selectedPaymentMethod"
              @onSelectedValueChange="emit('update:selectedPaymentMethod', $event)"
              @onShowNewFormCancel="emit('onShowNewFormCancel')"
              @onZuoraCallbackSuccess="emit('onZuoraCallbackSuccess', $event)"
              @onZuoraCallbackError="emit('onZuoraCallbackError', $event)"
              @onZuoraClientValidationError="(...args) => emit('onZuoraClientValidationError', ...args)"
            />
          </BaseCard>
        </div>
        <div class="rc-billing-form tw:mt-6">
          <h3 class="tw:font-bold tw:mb-1" ref="billingFormTitle">
            {{ $t('paymentMethods.billingAddress.heading') }}
          </h3>
          <BaseCard noPadding class="tw:px-4 tw:py-6">
            <BillingInformationForm
              ref="billingForm"
              :initialBillingFormValues="existingBillingInformation"
              @onValidateAllSuccess="emit('onValidateAllSuccess', $event)"
              @onValidateAllError="emit('onValidateAllError')"
              @setBillingFormValid="emit('setBillingFormValid', $event)"
            />
          </BaseCard>
        </div>
        <div class="rc-payment-footer tw:mt-6 tw:md:mt-0">
          <div class="sticky">
            <h3 class="tw:font-bold tw:mb-1">{{ $t('business_studio_plus_checkout_summary') }}</h3>
            <BaseCard noPadding class="tw:px-4 tw:py-6">
              <div class="tw:flex tw:items-start tw:flex-col">
                <p class="tw:font-bold tw:text-xl">{{ $t('navigation_menu_rate_connect') }}</p>
                <p class="tw:mb-6">
                  {{ $t('check_out_page_rc_campaign') }}
                </p>
                <p
                  class="tw:mb-6 tw:font-bold tw:w-full tw:flex tw:justify-between tw:border-b tw:mb-6 tw:pb-5 tw:border-gray-300"
                >
                  {{ $t('check_out_page_rc_commission_rate') }}
                  <span>{{ formatPercentage(language, selectedCommissionLevel, 0, 0) }}</span>
                </p>
                <div v-if="isFromMarketingLink" class="tw:w-full tw:mb-6">
                  <RCVoucherField
                    :voucherData="rcVoucher"
                    :isLoading="isVoucherFetching"
                    :initialCode="appliedCode"
                    @applyVoucherCode="emit('applyVoucherCode', $event)"
                  />
                </div>
                <div class="tw:flex tw:gap-3 tw:items-center tw:mb-6">
                  <input
                    id="rate-connect-terms"
                    type="checkbox"
                    class="tw:hover:cursor-pointer tw:h-4 tw:w-4"
                    :checked="termsAccepted"
                    @change="emit('update:termsAccepted', $event.target.checked)"
                    data-testid="rate-connect-terms-checkbox"
                  />
                  <label for="rate-connect-terms" class="tw:grow tw:text-sm tw:leading-4 tw:mb-0 terms">
                    <i18n-t keypath="termsAndPolicies.accept1" tag="p" scope="global" class="tw:inline-block tw:mb-0">
                      <template class="tw:inline-flex">
                        <a :href="termsUrl" target="_blank" class="tw:text-blue-700 tw:ml-[2px]">{{
                          $t('legal.termsAndConditions')
                        }}</a>
                      </template>
                    </i18n-t>
                  </label>
                </div>
                <MainButton
                  class="tw:w-full tw:mb-6"
                  dataTestId="rc-checkout-start-sub-button"
                  :disabled="!paymentEnabled || rcCampaignCreationInProgress"
                  @click="emit('onStartCampaign')"
                >
                  <SmallDotLoader color="#fff" v-if="rcCampaignCreationInProgress" />
                  <span v-else>{{ $t('check_out_page_rc_cta') }}</span></MainButton
                >

                <div class="tw:flex tw:items-start tw:mb-4 tw:gap-3">
                  <Lock class="tw:size-6" />
                  <span class="tw:text-base tw:text-gray-800">{{
                    $t('checkoutBasket.detailsEntry.secureConnection.recurringFee')
                  }}</span>
                </div>
                <div class="tw:flex tw:items-start tw:mb-4 tw:gap-3">
                  <Coins class="tw:size-6" /><span class="tw:text-base tw:text-gray-800">{{
                    $t('checkoutBasket.detailsEntry.payment.usageBased')
                  }}</span>
                </div>
                <div class="tw:flex tw:items-start tw:mb-4 tw:gap-3">
                  <CircleInfo class="tw:fill-none tw:size-6" />
                  <span class="tw:text-base tw:text-gray-800">{{ $t('check_out_page_rc_bookings_info') }}</span>
                </div>
                <div class="tw:flex tw:items-start tw:mb-4 tw:gap-3">
                  <CalendarIcon class="tw:size-6" />
                  <span class="tw:text-base tw:text-gray-800">{{ $t('check_out_page_rc_invoicing') }}</span>
                </div>
                <div class="tw:flex tw:items-start tw:gap-3">
                  <GearWheel class="tw:size-6" />
                  <span class="tw:text-base tw:text-gray-800">{{ $t('check_out_page_rc_settings') }}</span>
                </div>
              </div>
            </BaseCard>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import Lock from '@/components/icons/Lock.svg?component'
import Coins from '@/components/icons/Coins.svg?component'
import CalendarIcon from '@/components/icons/CalendarIcon.svg?component'
import CircleInfo from '@/components/icons/CircleInfo.svg?component'
import GearWheel from '@/components/icons/GearWheel.svg?component'
import BillingInformationForm from '@/components/payment/BillingInformationForm.vue'
import PaymentMethodForm from '@/components/payment/PaymentMethodForm.vue'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import PaymentCurrencySelector from '@/components/studioPlus/PaymentCurrencySelector.vue'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'
import { formatPercentage } from '@/utils/Utility.ts'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import RCVoucherField from '@/components/rateConnect/setup/RCVoucherField.vue'

defineProps({
  isLoading: Boolean,
  isExistingPaymentMethodsLoading: Boolean,
  isAvailablePaymentMethodsLoading: Boolean,
  isError: Boolean,
  zuoraAccountData: Object,
  existingPaymentMethods: Array,
  availablePaymentMethodsData: Array,
  paypalConfig: Object,
  currencyCode: String,
  selectedAccommodationId: [String, Number],
  termsAccepted: Boolean,
  selectedPaymentMethod: [String, Number],
  paymentEnabled: Boolean,
  existingBillingInformation: Object,
  errorInfo: [String, Boolean],
  isFetching: Boolean,
  selectedCommissionLevel: Number,
  language: String,
  rcCampaignCreationInProgress: Boolean,
  isFromMarketingLink: Boolean,
  rcVoucher: Object,
  isVoucherFetching: Boolean,
  appliedCode: String,
})

const emit = defineEmits([
  'update:termsAccepted',
  'update:selectedPaymentMethod',
  'onShowNewFormCancel',
  'onZuoraCallbackSuccess',
  'onZuoraCallbackError',
  'onZuoraClientValidationError',
  'onValidateAllSuccess',
  'onValidateAllError',
  'setBillingFormValid',
  'onStartCampaign',
  'applyVoucherCode',
])

const billingForm = ref(null)
const billingFormTitle = ref(null)

const termsUrl = computed(() => {
  return `${window.location.origin}/home/terms-and-conditions?app=rate-connect`
})

defineExpose({
  billingForm,
  billingFormTitle,
})
</script>

<style scoped>
.rc-payment-grid {
  display: block;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    grid-template-areas:
      'payment-currency payment-footer'
      'payment-form payment-footer'
      'billing-form payment-footer';
    column-gap: 4rem;
  }
}
.rc-payment-currency {
  grid-area: payment-currency;
}
.rc-payment-form {
  grid-area: payment-form;
}
.rc-payment-footer {
  grid-area: payment-footer;
}
.sticky {
  position: sticky;
  top: 120px;
  right: 0;
}
.rc-billing-form {
  grid-area: billing-form;
}
</style>
